import { useMemo } from 'react';
import PropTypes from 'prop-types';
import Score from '../score/Score';
import { SCAN_SANDBOX_STATUS } from '@mdc/constants';
import { useTranslation } from 'react-i18next';

import './SandboxScore.scss';

const SANDBOX_MAX_SCORE = 10;

const SandboxScore = ({ resultsLoading, scanning, fileImage, sandboxResults, category, hasButton, link, buttonText, onButtonClick, buttonDisabled, isFileSupport }) => {
    const { t, ready } = useTranslation();
    const sandboxScanProgress = sandboxResults?.progress ?? null;
    const sandboxScanProgressStatus = sandboxResults?.progressStatus ?? null;

    const scoreSandboxDom = useMemo(() => {
        const sandboxIsProcessing = resultsLoading || !!scanning || !sandboxResults || sandboxResults.status === SCAN_SANDBOX_STATUS.loading || sandboxResults.status === SCAN_SANDBOX_STATUS.processing;
        const sandboxScoreProcessed = sandboxResults?.results?.score;

        const sandboxProcessText = !sandboxResults ? t('Waiting for Sandbox') :
            sandboxResults.status === SCAN_SANDBOX_STATUS.loading ? t('Loading') :
                sandboxResults.status === SCAN_SANDBOX_STATUS.processing ?
                    (sandboxScanProgress ? t('Processing [{{ sandboxScanProgress }}%]', { sandboxScanProgress }) :
                        sandboxScanProgressStatus === SCAN_SANDBOX_STATUS.scheduled ? t('Scheduled') : t('Processing')) :
                    '';

        const sandboxDetection = sandboxResults?.results?.infected && sandboxScoreProcessed > 6 || sandboxResults?.status === SCAN_SANDBOX_STATUS.error ? 'detected' :
                sandboxResults?.results?.infected && sandboxScoreProcessed > 1 && sandboxScoreProcessed <= 6 ? 'suspicious' :
                    !sandboxResults?.results ? 'notPerformed' : 'notDetected';

        const sandboxInfo = sandboxResults?.status === SCAN_SANDBOX_STATUS.error ? sandboxResults.message :
            sandboxResults?.needEmailSubmit ? t('Blocked') :
                (hasButton && !isFileSupport) ? t('Filetype is not supported') :
                    (hasButton && !sandboxResults?.results) ? t('No dynamic analysis performed') :
                        sandboxResults?.results?.infected && sandboxScoreProcessed > 6 ? t('Malicious activity detected') :
                            sandboxResults?.results?.infected && sandboxScoreProcessed > 1 && sandboxScoreProcessed <= 6 ? t('Suspicious activity detected') :
                                t('No suspicious activity detected');

        const SANDBOX_BADGE_MESSAGE = [
            { minScore: 0, maxScore: 1, color: 'success', message: t('No threat detected') },
            { minScore: 2, maxScore: 6, color: 'warning', message: t('Suspicious') },
            { minScore: 7, maxScore: 10, color: 'danger', message: t('Malicious') }
        ];

        const sandboxBadge = SANDBOX_BADGE_MESSAGE.filter((entry) => sandboxScoreProcessed >= entry.minScore && sandboxScoreProcessed <= entry.maxScore)[0] || undefined;

        return <Score
            category={category}
            info={sandboxInfo}
            detection={sandboxDetection}
            score={sandboxScoreProcessed}
            numberOfEngines={SANDBOX_MAX_SCORE}
            link={link}
            badge={sandboxBadge?.message}
            buttonText={buttonText}
            onButtonClick={onButtonClick}
            buttonDisabled={buttonDisabled}
            processImgSrc={fileImage?.processing?.publicURL}
            isProcessing={sandboxIsProcessing}
            processingText={sandboxProcessText}
            badgeColor={sandboxBadge?.color}
            scoreColor={sandboxBadge?.color}
            hasButton={hasButton}
        />;
    }, [resultsLoading, scanning, sandboxResults, sandboxScanProgress, sandboxScanProgressStatus, category, t]);

    if (!ready) {
        return null;
    }

    return <>
        {scoreSandboxDom}
    </>;
};

SandboxScore.propTypes = {
    resultsLoading: PropTypes.bool,
    scanning: PropTypes.string,
    fileImage: PropTypes.object.isRequired,
    sandboxResults: PropTypes.object,
    category: PropTypes.string,
    hasButton: PropTypes.bool,
    link: PropTypes.string,
    buttonText: PropTypes.any,
    onButtonClick: PropTypes.func,
    buttonDisabled: PropTypes.bool,
    isFileSupport: PropTypes.any
};

SandboxScore.defaultProps = {
    hasButton: false
};

export default SandboxScore;
